import type { CommentMention, CommentThreadMeta } from "@certa/types";
import { useContext, useMemo } from "react";
import { ChannelContext } from "../comments.context";
import { prepareMentionOptions } from "../comments.utils";
import { useEnableSearchAPIOnMentionableThreadUsers } from "@certa/common/toggles/useEnableSearchAPIOnMentionableThreadUsers";

// TODO: Since allThreads is always generated in the model, the reference keeps changing
// hence the useMentions hook returns new reference of mentions each time, which
// in turn will execute all operation again that depend on it's reference.
// (example: useCommentMessageFormat)
// For performance improvements, certain values that don't change should not be altered.
// to prevent unnecessary re-renders.
/**
 * Provides a list of mentionable groups and users for a particular thread.
 * When feature toggle is ON: Returns only groups (users fetched dynamically via API)
 * When feature toggle is OFF: Returns both groups and users (original behavior)
 *
 * @param threadId - The ID of the thread to get mentions for
 * @param forHighlightingInMessage - When true, returns all mentions (groups + users)
 *   regardless of feature toggle state. This is needed for highlighting existing mentions
 *   in messages, as we need the complete list to properly highlight both user and group
 *   mentions that are already present in the message content. When toggle is ON, uses
 *   channelUsers instead of threadUsers for a broader scope of mentionable users.
 */
export const useMentions = (
  threadId: number,
  forHighlightingInMessage?: boolean
) => {
  const channel = useContext(ChannelContext);
  const { allThreads = [], channelUsers = [] } = channel;
  const isSearchAPIEnabled = useEnableSearchAPIOnMentionableThreadUsers();

  const threadMeta = useMemo(
    () =>
      allThreads.find(
        (thread: CommentThreadMeta) => thread.threadId === threadId
      ),
    [allThreads, threadId]
  );

  const mentions: CommentMention[] = useMemo(() => {
    const allThreadUsers = threadMeta?.threadUsers || [];

    // When toggle is ON and forHighlightingInMessage is true, use channelUsers since channels API will not have user_mentions on thread level
    const searchAPIEnabledUsers = forHighlightingInMessage ? channelUsers : [];

    return prepareMentionOptions({
      threadUserGroups: threadMeta?.threadUserGroups || [],
      threadUsers: isSearchAPIEnabled ? searchAPIEnabledUsers : allThreadUsers
    });
  }, [threadMeta, isSearchAPIEnabled, forHighlightingInMessage, channelUsers]);
  return mentions;
};
